import { getSupabaseClient } from '@/integrations/supabase/client';
import { Tab, TabItem } from '@/types';
import { parseTimestamp } from '@/utils/dateFormat';
import {
  TAB_ITEM_MODIFIERS_EMBED,
  mapTabItemModifierRows,
  tabItemModifiersService,
} from '@/services/products/tabItemModifiersService';
import { isWeightPartialMove } from '@/utils/weightPricing';
import { syncTableAssignmentAtomicWithRetry } from './tabsService';

export interface SplitTabParams {
  sourceTabId: string;
  newCustomerName: string;
  itemsToMove: Array<{
    itemId: string;
    quantityToMove: number;
  }>;
  tableId?: string | null;
  userId: string;
  organizationId: string;
}

/** Snapshot-driven move payload (offline-replayable). No DB re-reads of source rows. */
export interface MoveItemsBetweenTabsFromSnapshotsParams {
  organizationId: string;
  sourceTabId: string;
  targetTabId: string;
  items: Array<{
    itemId: string;
    quantityToMove: number;
    sourceQuantity: number;
    isFullMove: boolean;
    newItemId?: string;
    productId: string;
    productName: string;
    productCategory: string;
    categoryId?: string | null;
    unitPrice: number;
    discount: number;
    notes?: string | null;
    customerPosition?: number | null;
    weightKg?: number | null;
    remainingWeightKg?: number | null;
    remainingUnitPrice?: number | null;
    promotionComponents?: Array<{
      componentProductId: string;
      quantity: number;
      productName: string;
    }>;
    modifiers?: Array<{
      modifierId: string | null;
      name: string;
      price: number;
    }>;
  }>;
}

/**
 * Moves (fully or partially) tab items from a source tab into an already
 * existing target tab. Used by `splitTab` (target = newly created tab).
 * Cross-tab moves use `moveItemsBetweenTabsFromSnapshots` instead.
 */
async function moveItemsToTab(
  itemsToMove: Array<{ itemId: string; quantityToMove: number }>,
  sourceTabId: string,
  targetTabId: string,
  organizationId: string,
): Promise<void> {
  const supabase = getSupabaseClient();

  for (const itemToMove of itemsToMove) {
    const { itemId, quantityToMove } = itemToMove;

    if (!Number.isInteger(quantityToMove) || quantityToMove <= 0) {
      throw new Error(`Invalid quantityToMove for item ${itemId}`);
    }

    const { data: currentItem, error: itemError } = await supabase
      .from('tab_items')
      .select('*')
      .eq('id', itemId)
      .eq('tab_id', sourceTabId)
      .eq('organization_id', organizationId)
      .single();

    if (itemError) {
      console.error('Error fetching item:', itemError);
      throw itemError;
    }

    if (!currentItem) {
      throw new Error(`Item with id ${itemId} not found`);
    }

    if (quantityToMove >= currentItem.quantity) {
      const { error: moveErr } = await supabase.from('tab_items').update({ tab_id: targetTabId }).eq('id', itemId);

      if (moveErr) {
        console.error('Error moving tab item:', moveErr);
        throw moveErr;
      }
    } else {
      const { error: reduceErr } = await supabase
        .from('tab_items')
        .update({ quantity: currentItem.quantity - quantityToMove })
        .eq('id', itemId);

      if (reduceErr) {
        console.error('Error reducing tab item quantity:', reduceErr);
        throw reduceErr;
      }

      const newItemData: Record<string, unknown> = {
        tab_id: targetTabId,
        product_id: currentItem.product_id,
        product_name: currentItem.product_name,
        product_category: currentItem.product_category,
        quantity: quantityToMove,
        unit_price: currentItem.unit_price,
        discount: currentItem.discount,
        created_at: new Date().toISOString(),
        organization_id: organizationId,
      };
      if (currentItem.category_id) newItemData.category_id = currentItem.category_id;
      if (currentItem.notes) newItemData.notes = currentItem.notes;
      if (currentItem.customer_position) newItemData.customer_position = currentItem.customer_position;
      if (currentItem.weight_kg != null) newItemData.weight_kg = currentItem.weight_kg;

      const { data: insertedItem, error: insertErr } = await supabase
        .from('tab_items')
        .insert([newItemData])
        .select()
        .single();

      if (insertErr) {
        console.error('Error inserting moved tab item:', insertErr);
        throw insertErr;
      }

      const { data: promoComponents, error: compFetchErr } = await supabase
        .from('tab_item_promotion_components')
        .select('component_product_id, quantity, product_name')
        .eq('tab_item_id', itemId);

      if (compFetchErr) {
        console.error('Error fetching promotion components:', compFetchErr);
        throw compFetchErr;
      }

      if (promoComponents && promoComponents.length > 0 && insertedItem?.id) {
        const rows = promoComponents.map((c: any) => ({
          tab_item_id: insertedItem.id as string,
          component_product_id: c.component_product_id,
          quantity: c.quantity,
          product_name: c.product_name,
          created_at: new Date().toISOString(),
        }));
        const { error: compInsErr } = await supabase.from('tab_item_promotion_components').insert(rows);
        if (compInsErr) {
          console.error('Error copying promotion components:', compInsErr);
          throw compInsErr;
        }
      }

      if (insertedItem?.id) {
        const modifiersByItem = await tabItemModifiersService.getByTabItemIds([itemId]);
        await tabItemModifiersService.insertForTabItem(
          insertedItem.id as string,
          organizationId,
          modifiersByItem.get(itemId) ?? []
        );
      }
    }
  }
}

export const tabSplitService = {
  async splitTab(params: SplitTabParams): Promise<Tab> {
    const supabase = getSupabaseClient();
    const { sourceTabId, newCustomerName, itemsToMove, userId, organizationId, tableId } = params;

    try {
      // Start a transaction by creating the new tab first
      const { data: newTab, error: newTabError } = await supabase
        .from('tabs')
        .insert([{
          customer_name: newCustomerName,
          created_by: userId,
          organization_id: organizationId,
          is_open: true,
          discount: 0
        }])
        .select()
        .single();

      if (newTabError) {
        console.error('Error creating new tab:', newTabError);
        throw newTabError;
      }

      await moveItemsToTab(itemsToMove, sourceTabId, newTab.id, organizationId);

      // Fetch the complete new tab with its items
      const { data: completeNewTab, error: fetchError } = await supabase
        .from('tabs')
        .select(`
          *,
          tab_items!left (
            id,
            product_id,
            product_name,
            product_category,
            quantity,
            unit_price,
            discount,
            notes,
            customer_position,
            created_at,
            ${TAB_ITEM_MODIFIERS_EMBED}
          )
        `)
        .eq('id', newTab.id)
        .single();

      if (fetchError) {
        console.error('Error fetching complete new tab:', fetchError);
        throw fetchError;
      }

      if (tableId) {
        const { data: openRows, error: openError } = await supabase
          .from('tabs')
          .select('id')
          .eq('table_id', tableId)
          .eq('is_open', true);
        if (openError) throw openError;
      
        const tabIds = [
          ...new Set([
            ...(openRows ?? []).map((row) => row.id as string),
            sourceTabId,
            newTab.id,
          ]),
        ];
      
        await syncTableAssignmentAtomicWithRetry(tableId, tabIds);
      }

      // Transform the data to match our Tab interface
      const tabItems = completeNewTab.tab_items || [];
      const sortedItems = tabItems
        .filter(item => item != null)
        .sort((a, b) => 
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

      const transformedTab: Tab = {
        id: completeNewTab.id,
        customerName: completeNewTab.customer_name,
        discount: Number(completeNewTab.discount || 0),
        isOpen: completeNewTab.is_open || false,
        isCredit: completeNewTab.is_credit || false,
        isQuickSale: false,
        createdAt: new Date(completeNewTab.created_at),
        updatedAt: new Date(completeNewTab.updated_at),
        createdBy: completeNewTab.created_by,
        organizationId: completeNewTab.organization_id,
        tableId: completeNewTab.table_id ?? null,
        items: sortedItems.map((item: any) => ({
          id: item.id,
          productId: item.product_id,
          product: {
            id: item.product_id,
            name: item.product_name,
            category: item.product_category,
            cost: 0,
            price: Number(item.unit_price),
            organizationId: organizationId
          },
          quantity: item.quantity,
          unitPrice: Number(item.unit_price),
          discount: Number(item.discount || 0),
          notes: item.notes || undefined,
          customerPosition: item.customer_position ?? undefined,
          createdAt: parseTimestamp(item.created_at),
          modifiers: mapTabItemModifierRows(item.tab_item_modifiers),
        })) as TabItem[]
      };

      return transformedTab;

    } catch (error) {
      console.error('Tab split service error:', error);
      throw error;
    }
  },

  /**
   * Offline-replayable move: applies snapshotted item fields with absolute
   * quantity writes and client-generated ids. Idempotent on replay.
   */
  async moveItemsBetweenTabsFromSnapshots(
    params: MoveItemsBetweenTabsFromSnapshotsParams
  ): Promise<void> {
    const { organizationId, sourceTabId, targetTabId, items } = params;

    if (sourceTabId === targetTabId) {
      throw new Error('Source and target tab must be different');
    }

    const supabase = getSupabaseClient();

    try {
      for (const entry of items) {
        const isWeightPartial = isWeightPartialMove(entry);

        if (!isWeightPartial) {
          if (!Number.isInteger(entry.quantityToMove) || entry.quantityToMove <= 0) {
            throw new Error(`Invalid quantityToMove for item ${entry.itemId}`);
          }
        } else if (
          !Number.isFinite(entry.weightKg) ||
          entry.weightKg <= 0 ||
          !Number.isFinite(entry.remainingWeightKg) ||
          entry.remainingWeightKg <= 0
        ) {
          throw new Error(`Invalid weight split for item ${entry.itemId}`);
        }

        if (entry.isFullMove) {
          const { data: movedRows, error: moveErr } = await supabase
            .from('tab_items')
            .update({ tab_id: targetTabId })
            .eq('id', entry.itemId)
            .eq('tab_id', sourceTabId)
            .eq('organization_id', organizationId)
            .select('id');

          if (moveErr) {
            console.error('Error moving tab item:', moveErr);
            throw moveErr;
          }

          if (!movedRows || movedRows.length === 0) {
            const { data: alreadyOnTarget, error: checkErr } = await supabase
              .from('tab_items')
              .select('id')
              .eq('id', entry.itemId)
              .eq('tab_id', targetTabId)
              .eq('organization_id', organizationId)
              .maybeSingle();

            if (checkErr) throw checkErr;
            if (alreadyOnTarget) continue;

            throw new Error(`Item with id ${entry.itemId} not found on source tab`);
          }
        } else {
          if (!entry.newItemId) {
            throw new Error(`Missing newItemId for partial move of item ${entry.itemId}`);
          }

          if (isWeightPartial) {
            const { error: reduceErr } = await supabase
              .from('tab_items')
              .update({
                quantity: 1,
                weight_kg: entry.remainingWeightKg,
                unit_price: entry.remainingUnitPrice,
              })
              .eq('id', entry.itemId)
              .eq('organization_id', organizationId);

            if (reduceErr) {
              console.error('Error reducing tab item weight:', reduceErr);
              throw reduceErr;
            }
          } else {
            const remainingQuantity = entry.sourceQuantity - entry.quantityToMove;
            const { error: reduceErr } = await supabase
              .from('tab_items')
              .update({ quantity: remainingQuantity })
              .eq('id', entry.itemId)
              .eq('organization_id', organizationId);

            if (reduceErr) {
              console.error('Error reducing tab item quantity:', reduceErr);
              throw reduceErr;
            }
          }

          const newItemData: Record<string, unknown> = {
            id: entry.newItemId,
            tab_id: targetTabId,
            product_id: entry.productId,
            product_name: entry.productName,
            product_category: entry.productCategory,
            quantity: isWeightPartial ? 1 : entry.quantityToMove,
            unit_price: entry.unitPrice,
            discount: entry.discount,
            created_at: new Date().toISOString(),
            organization_id: organizationId,
          };
          if (entry.categoryId) newItemData.category_id = entry.categoryId;
          if (entry.notes) newItemData.notes = entry.notes;
          if (entry.customerPosition) newItemData.customer_position = entry.customerPosition;
          if (isWeightPartial) {
            newItemData.weight_kg = entry.weightKg;
          }

          const { data: upsertedRows, error: insertErr } = await supabase
            .from('tab_items')
            .upsert([newItemData], { onConflict: 'id', ignoreDuplicates: true })
            .select('id');

          if (insertErr) {
            console.error('Error inserting moved tab item:', insertErr);
            throw insertErr;
          }

          const insertedNewRow =
            Array.isArray(upsertedRows) &&
            upsertedRows.some((row) => row.id === entry.newItemId);

          if (entry.modifiers?.length) {
            // Idempotent upsert, so it also completes a replay after a partial failure.
            await tabItemModifiersService.insertForTabItem(
              entry.newItemId,
              organizationId,
              entry.modifiers
            );
          }

          if (
            insertedNewRow &&
            entry.promotionComponents &&
            entry.promotionComponents.length > 0
          ) {
            const rows = entry.promotionComponents.map((c) => ({
              tab_item_id: entry.newItemId as string,
              component_product_id: c.componentProductId,
              quantity: c.quantity,
              product_name: c.productName,
              created_at: new Date().toISOString(),
            }));
            const { error: compInsErr } = await supabase
              .from('tab_item_promotion_components')
              .insert(rows);
            if (compInsErr) {
              // Replay may already have components; unique conflicts are non-fatal
              // only if we can't distinguish — prefer throw for unexpected errors.
              if (compInsErr.code === '23505') continue;
              console.error('Error copying promotion components:', compInsErr);
              throw compInsErr;
            }
          }
        }
      }
    } catch (error) {
      console.error('Move tab items from snapshots service error:', error);
      throw error;
    }
  },
};
